import { useRef, useState, useEffect } from 'react'
import styled from 'styled-components'
import { useForm } from 'react-hook-form'
import { useSelector, useDispatch } from 'react-redux'
import { Link, useParams } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { Tabs, Tab, Box } from '@mui/material'
import LoadingSpinnerGreen from 'components/ui/LoadingSpinnerGreen'
import { RootState } from 'store'
import { selectCountriesWithClubs } from 'store/countries/slice'
import { mapUserChallengeToFormData } from 'utils/challenges'
import ChallengeReadOnlyPhase1 from 'components/form/phases/ChallengeReadOnlyPhase1'
import ChallengeReadOnlyPhase2 from 'components/form/phases/ChallengeReadOnlyPhase2'
import ChallengeReadOnlyPhase3 from 'components/form/phases/ChallengeReadOnlyPhase3'
import ChallengeReadOnlyPhase4 from 'components/form/phases/ChallengeReadOnlyPhase4'
import { selectUserChallenge } from 'store/userChallenge/slice'
import { getUserChallenge } from 'store/userChallenge/thunks'
import UserChallengeStatusBadge from 'components/userChallenge/StatusBadge'
import { ChevronLeftIcon } from 'assets/icons/chevron-left'
import { ClientRoute } from 'routes/client'

interface ParamTypes extends ParamTypesBase {
  id: string
}

export default function ChallengeReadOnly(): JSX.Element {
  const { id: paramId } = useParams<ParamTypes>()
  const id = paramId || ''
  const [phase, setPhase] = useState<string>('1')
  const [currentId, setCurrentId] = useState<string>('')

  const { countries } = useSelector(selectCountriesWithClubs)

  const formElement = useRef<HTMLFormElement>(null)
  const { userChallenge, status } = useSelector(selectUserChallenge)
  const clubs = useSelector((state: RootState) => state.clubs.clubs)
  const form = useForm({
    shouldUnregister: false,
    disabled: true,
  })
  const { reset } = form
  const dispatch = useDispatch()
  const intl = useIntl()

  useEffect(() => {
    if (id && (status === 'idle' || currentId !== id)) {
      setCurrentId(id)
      dispatch(getUserChallenge(id))
    }
  }, [id, currentId, status, dispatch, userChallenge])

  // Generate initial form data
  useEffect(() => {
    const challengeIsFetched = id === userChallenge?.challengeId
    if (id && status === 'succeeded' && challengeIsFetched && userChallenge.challengeId !== '-1') {
      reset({ ...mapUserChallengeToFormData(userChallenge, clubs, countries) })
    }
  }, [id, userChallenge, status, reset, intl, clubs, countries])

  const handleChange = (event, tabValue) => {
    setPhase(tabValue)
  }

  return (
    <PageContainer>
      <Box
        sx={{
          justifyContent: 'flex-start',
          flexDirection: 'row',
          display: 'flex',
          width: '100%',
          paddingLeft: '100px',
        }}
      >
        <Link
          style={{
            color: '#0BB163',
            gap: '8px',
            display: 'inline-flex',
            alignItems: 'center',
          }}
          to={ClientRoute.ADMIN_CHALLENGE(id)}
        >
          <ChevronLeftIcon width="20px" height="20px" circleFill="#0BB163" pathStroke="#EDEFF1" />
          Back to challenge
        </Link>
      </Box>
      <SectionContainer>
        <ChallengeStatusBadge userChallenge={userChallenge} />
        <TabContainer value={phase} onChange={handleChange}>
          <Tab value="1" label={intl.formatMessage({ id: 'challenges.typeAndScheduling' })} />
          <Tab value="2" label={intl.formatMessage({ id: 'challenges.targeting' })} />
          <Tab value="3" label={intl.formatMessage({ id: 'challenges.descriptions' })} />
          <Tab value="4" label={intl.formatMessage({ id: 'challenges.branding' })} />
        </TabContainer>
      </SectionContainer>

      {status === 'loading' || (userChallenge.challengeId !== '-1' && !id) ? (
        <SpinnerContainer>
          <LoadingSpinnerGreen size={72} />
        </SpinnerContainer>
      ) : (
        <form ref={formElement}>
          {phase === '1' && <ChallengeReadOnlyPhase1 challenge={userChallenge} form={form} />}
          {phase === '2' && <ChallengeReadOnlyPhase2 challenge={userChallenge} form={form} />}
          {phase === '3' && <ChallengeReadOnlyPhase3 form={form} />}
          {phase === '4' && <ChallengeReadOnlyPhase4 challenge={userChallenge} />}
        </form>
      )}
    </PageContainer>
  )
}

const PageContainer = styled.div`
  padding: 8px;
  display: flex;
  flex-direction: column;
  width: 100%;
`

const SectionContainer = styled.div`
  background-color: #fff;
  margin: 10px;
  padding: 20px;

  & h2 {
    font-weight: 600;
    font-style: italic;
    font-size: 1.5rem;
  }

  & > button {
    margin-right: 10px;
  }
`

const TabContainer = styled(Tabs)`
  width: 100%;

  & .MuiTabs-indicator {
    height: 0;
  }

  & button {
    margin: 10px;
    border-bottom: 6px solid;
    border-color: ${(props) => props.theme.customPalette.mediumGray};
    padding: 6px;
    width: 165px;

    &.Mui-selected {
      color: ${(props) => props.theme.palette.primary.main};
      border-color: ${(props) => props.theme.palette.primary.main};
    }
  }
`

const SpinnerContainer = styled.div`
  display: flex;
  width: 100%;
  margin: 50px auto;
  justify-content: center;
`

const ChallengeStatusBadge = styled(UserChallengeStatusBadge)`
  float: right;
  padding: 2px 10px;
  font-size: 14px;
`
